import type { SelectOption } from './Select.types';

const isRecord = (option: SelectOption): option is Record<string, unknown> =>
  typeof option === 'object' && option !== null;

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

export const resolveOptionLabel = (
  option: SelectOption,
  optionLabel?: string | ((option: SelectOption) => string),
): string => {
  if (typeof optionLabel === 'function') return optionLabel(option);
  if (typeof optionLabel === 'string' && isRecord(option)) {
    return String(option[optionLabel] ?? '');
  }
  return String(option);
};

export const resolveOptionValue = (
  option: SelectOption,
  optionValue?: string | ((option: SelectOption) => string | number),
): string | number => {
  if (typeof optionValue === 'function') return optionValue(option);
  if (typeof optionValue === 'string' && isRecord(option)) {
    return option[optionValue] as string | number;
  }
  return isRecord(option) ? resolveOptionLabel(option) : option;
};

export const resolveOptionDisabled = (
  option: SelectOption,
  optionDisabled?: string | ((option: SelectOption) => boolean),
): boolean => {
  if (typeof optionDisabled === 'function') return optionDisabled(option);
  if (!isRecord(option)) return false;
  return Boolean(option[optionDisabled ?? 'disabled']);
};

export const filterOptions = (
  options: SelectOption[],
  query: string,
  optionLabel?: string | ((option: SelectOption) => string),
): SelectOption[] => {
  const normalizedQuery = normalize(query.trim());
  if (!normalizedQuery) return options;

  return options.filter((option) =>
    normalize(resolveOptionLabel(option, optionLabel)).includes(
      normalizedQuery,
    ),
  );
};

export type SelectFlatRow =
  | { kind: 'group'; option: SelectOption; label: string }
  | { kind: 'option'; option: SelectOption };

export const flattenGroups = (
  options: SelectOption[],
  groupValues?: string,
  groupLabel?: string,
  optionLabel?: string | ((option: SelectOption) => string),
): SelectFlatRow[] => {
  if (!groupValues) {
    return options.map((option) => ({ kind: 'option', option }));
  }

  return options.flatMap((group) => {
    if (!isRecord(group)) return [{ kind: 'option', option: group } as const];

    const children = group[groupValues];
    if (!Array.isArray(children) || children.length === 0) return [];

    return [
      {
        kind: 'group',
        option: group,
        label: resolveOptionLabel(group, groupLabel ?? optionLabel),
      } as const,
      ...(children as SelectOption[]).map(
        (option) => ({ kind: 'option', option }) as const,
      ),
    ];
  });
};

export const canCreateFromQuery = (
  query: string,
  options: SelectOption[],
  optionLabel?: string | ((option: SelectOption) => string),
  createValidator?: (query: string) => boolean,
): boolean => {
  const trimmed = query.trim();
  if (!trimmed) return false;

  const alreadyExists = options.some(
    (option) => resolveOptionLabel(option, optionLabel) === trimmed,
  );
  if (alreadyExists) return false;

  return createValidator ? createValidator(trimmed) : true;
};

export const toSafeId = (value: string | number): string =>
  String(value).replace(
    /[^A-Za-z0-9-]/g,
    (char) => `_${char.charCodeAt(0).toString(16)}_`,
  );
